import React, { useState, useEffect, useMemo } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Toolbar,
  AppBar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Container,
  Paper,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Link,
} from "@mui/material";
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";

import DashboardIcon from "@mui/icons-material/Dashboard";
import TreeViewIcon from "@mui/icons-material/AccountTree";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MenuIcon from "@mui/icons-material/Menu";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LogoutIcon from "@mui/icons-material/Logout";
import AppsIcon from "@mui/icons-material/Apps";
import InfoIcon from "@mui/icons-material/Info";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

// Import the new logo component
import DreamyAppsLogo from "../components/DreamyAppsLogo";

// Screens
import EarningsSummary from "./EarningsSummary";
import InviteFriends from "./InviteFriends";
import ReferralTree from "./ReferralTreeLazy";
import ParentChildRelationships from "./ParentChildRelationships";
import TokenLedger from "./TokenLedger";
import UpdateProfile from "./UpdateProfile";
// import PaymentSettings from "./PaymentSettings"; // Hidden - not needed with auto-processing
import WithdrawHub from "./WithdrawHub";
import VideoUpload from "./VideoUpload";
import UserVideoList from "./UserVideoList";
import Apps from "./Apps";

// Channel components
import ChannelsList from "./ChannelsList";
import ChannelDetail from "./ChannelDetail";
import ChannelManage from "./ChannelManage";
import ChannelEdit from "./ChannelEdit";
import VideoEdit from "./VideoEdit";
import StripeKYCVerification from "./StripeKYCVerification";
import { shouldShowKYC, normalizeKYCStatus } from '../utils/kycUtils';
import KYCTestComponent from '../components/KYCTestComponent';
import { API } from '../config/apiConfig';

const drawerWidth = 260;
const mobileDrawerWidth = 200; // Reduced width for mobile

function UserDashboard({ onLogout, isAdmin, isDirector, isContentCreator, darkMode, onToggleDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for user profile and KYC status
  const [userProfile, setUserProfile] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [showKYC, setShowKYC] = useState(false);
  const [needsCountryUpdate, setNeedsCountryUpdate] = useState(false);
  const [isChild, setIsChild] = useState(false);
  
  // State for profile incomplete notification
  const [hasCNIC, setHasCNIC] = useState(true); // Default to true (no notification)
  const [isProfileComplete, setIsProfileComplete] = useState(true); // Default to true (no notification)
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  
  // State for mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Close mobile drawer when navigating
  const handleMobileNavClick = () => {
    setMobileOpen(false);
  };
  
  // Handle content creator menu click - close mobile drawer
  const handleContentCreatorClick = () => {
    setMobileOpen(false);
  };

  // Load user profile and determine KYC requirements
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        // Get user profile from localStorage or API
        const token = localStorage.getItem('token');
        if (token) {
          // Decode token to get user info
          const decoded = JSON.parse(atob(token.split('.')[1]));
          const userId = decoded.userId;
          
          // Fetch user profile from API to get actual CountryOfResidenceId from database
          // This is more reliable than JWT token which might not have all fields
          let countryOfResidenceId = null;
          let countryCode = null;
          let hasCountry = false;
          
          try {
            const profileResponse = await fetch(API.userProfile(userId), {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (profileResponse.ok) {
              const profileData = await profileResponse.json();
              const userData = profileData?.user || profileData || {};
              
              // Get CountryOfResidenceId from API response
              countryOfResidenceId = userData.CountryOfResidenceId || 
                                     userData.CountryOfResidenceID || 
                                     userData.countryOfResidenceId || 
                                     null;
              
              // Get CountryCode from API response (this is the country code for residence)
              countryCode = userData.CountryCode || 
                           userData.countryCode || 
                           decoded.countryCode || 
                           decoded.country || 
                           null;
              
              hasCountry = (countryOfResidenceId !== null && countryOfResidenceId !== undefined) ||
                          (countryCode !== null && countryCode !== undefined);
              
              console.log('🌍 Country of Residence Check (from API):', {
                countryOfResidenceId,
                countryCode,
                hasCountry,
                currentPath: location.pathname
              });
            } else {
              // Fallback to JWT token if API call fails
              console.warn('⚠️ Could not fetch user profile from API, using JWT token');
              countryCode = decoded.countryCode || decoded.country || null;
              countryOfResidenceId = decoded.countryOfResidenceId || decoded.CountryOfResidenceId || null;
              hasCountry = (countryCode !== null && countryCode !== undefined) || 
                          (countryOfResidenceId !== null && countryOfResidenceId !== undefined);
            }
          } catch (profileError) {
            console.error('❌ Error fetching user profile:', profileError);
            // Fallback to JWT token
            countryCode = decoded.countryCode || decoded.country || null;
            countryOfResidenceId = decoded.countryOfResidenceId || decoded.CountryOfResidenceId || null;
            hasCountry = (countryCode !== null && countryCode !== undefined) || 
                        (countryOfResidenceId !== null && countryOfResidenceId !== undefined);
          }
          
          // Note: Country update requirement removed from general navigation
          // Only enforced on redemption page (WithdrawHub.js)
          // Users can navigate freely to other pages
          
          const profile = {
            id: userId,
            username: decoded.username || '',
            firstName: decoded.firstName || '',
            lastName: decoded.lastName || '',
            email: decoded.email || '',
            country: countryCode,
            countryCode: countryCode,
            region: decoded.region || null,
            countryOfResidenceId: countryOfResidenceId,
          };
          setUserProfile(profile);
          setNeedsCountryUpdate(false); // Always false - no longer blocking navigation
          
          // Check if profile is complete (has CountryOfResidenceId)
          setIsProfileComplete(countryOfResidenceId !== null && countryOfResidenceId !== undefined);
          
          console.log('👤 User Profile for KYC:', profile);
          
          // Only check KYC if country code is available
          if (profile.countryCode) {
            // Determine if KYC should be shown (now async - checks database)
            const kycInfo = await shouldShowKYC(profile);
            console.log('🔍 KYC Info (from database):', kycInfo);
          setShowKYC(kycInfo.show);
          setKycStatus(prev => ({
            ...prev,
            regionRequired: kycInfo.required,
          }));
          
          // Load KYC status if required - DISABLED for performance
          // if (kycInfo.show) {
          //   try {
          //     const kycResponse = await fetch(API.kycStatus, {
          //       headers: {
          //         'Authorization': `Bearer ${token}`,
          //         'Content-Type': 'application/json'
          //       }
          //     });
          //     
          //     if (kycResponse.ok) {
          //       const kycData = await kycResponse.json();
          //       console.log('🧾 Raw KYC status (UserDashboard):', kycData);
          //       setKycStatus(normalizeKYCStatus(kycData));
          //     } else {
          //       console.warn('⚠️ Could not load KYC status, using default');
          //       setKycStatus({
          //         verified: false,
          //         status: 'not_started'
          //       });
          //     }
          //   } catch (kycError) {
          //     console.error('❌ Error loading KYC status:', kycError);
          //     setKycStatus({
          //       verified: false,
          //       status: 'not_started'
          //     });
          //   }
          // }
          
          // Set default KYC status without API call
                setKycStatus({
                  verified: false,
            status: 'not_started',
            regionRequired: kycInfo.required,
          });
          } else {
            console.log('⚠️ No country code available, skipping KYC check');
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, [location.pathname, navigate]);

  // Determine menu items based on user's country
  // Check if user is a child
  useEffect(() => {
    const checkIfChild = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(API.checkIfUserIsChild, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('🔍 Check if child response:', data);
          // Always set isChild based on data.isChild, regardless of success flag
          const isChildValue = data.isChild === true || data.isChild === 1 || data.isChild === '1' || data.isChild === 'true';
          console.log('🔍 Setting isChild to:', isChildValue, 'from data.isChild:', data.isChild, 'type:', typeof data.isChild);
          setIsChild(!!isChildValue); // Force boolean
        } else {
          console.error('❌ Failed to check if child:', response.status, response.statusText);
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Error response data:', errorData);
          setIsChild(false);
        }
      } catch (error) {
        console.error('Error checking if user is child:', error);
        setIsChild(false); // Default to false on error
      }
    };

    checkIfChild();
  }, []);

  // Check if user has CNIC (for Pakistan users)
  useEffect(() => {
    const checkCNIC = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(API.savedPaymentAccounts, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const accounts = Array.isArray(data?.accounts) ? data.accounts : [];
          const jazzCashAccount = accounts.find(acc => 
            (acc.Provider || acc.PaymentMethodName) === 'JazzCash'
          );
          const hasCNICValue = jazzCashAccount?.CNIC && 
                              jazzCashAccount.CNIC.trim().length > 0;
          setHasCNIC(hasCNICValue);
        }
      } catch (error) {
        console.error('Error checking CNIC:', error);
        setHasCNIC(true); // Default to true (no notification) on error
      }
    };

    checkCNIC();
  }, []);

  // Check both country and countryCode fields, case-insensitive
  const isPakistanUser = userProfile?.country?.toUpperCase() === 'PK' || 
                         userProfile?.countryCode?.toUpperCase() === 'PK';
  
  // Determine what's incomplete for Pakistan users (and not child)
  const profileIncomplete = isPakistanUser && !isProfileComplete && !isChild;
  const redemptionIncomplete = isPakistanUser && !hasCNIC && !isChild;
  
  // Show notification badge if either profile or redemption is incomplete
  const showProfileIncompleteNotification = profileIncomplete || redemptionIncomplete;
  
  // Get the appropriate notification message
  const getNotificationMessage = () => {
    if (profileIncomplete && redemptionIncomplete) {
      return {
        title: 'Your profile is incomplete',
        message: 'Please update your profile and redemption settings as soon as possible to receive payments.',
        buttonText: 'Go to Profile',
        navigateTo: '/dashboard/profile'
      };
    } else if (profileIncomplete) {
      return {
        title: 'Your profile is incomplete',
        message: 'Please update your profile as soon as possible.',
        buttonText: 'Go to Profile',
        navigateTo: '/dashboard/profile'
      };
    } else if (redemptionIncomplete) {
      return {
        title: 'Your redemption settings are incomplete',
        message: 'Please update your redemptions as soon as possible for payments.',
        buttonText: 'Go to Redemptions',
        navigateTo: '/dashboard/redemptions'
      };
    }
    return null;
  };
  
  const notificationMessage = getNotificationMessage();
  
  // Build menu items - hide redemptions for children
  // Use useMemo to recalculate when isChild changes
  const menuItems = useMemo(() => {
    const allMenuItems = [
      { key: "engagement", label: "Engagement Summary", icon: <DashboardIcon /> },
      { key: "activity", label: "Engagement Detail", icon: <HistoryIcon /> },
      { key: "profile", label: "My Profile", icon: <EditIcon /> },
      { key: "redemptions", label: "Redemptions", icon: <AccountBalanceWalletIcon /> },
      { key: "invite", label: "Grow My Community", icon: <SendIcon /> },
      { key: "community", label: "Community Network", icon: <TreeViewIcon /> },
      { key: "relationships", label: "Family Relationships", icon: <PersonAddIcon /> },
      { key: "apps", label: "My Apps", icon: <AppsIcon /> },
    ];

    // Filter out redemptions and family relationships if user is a child
    console.log('🔍 Filtering menu items, isChild:', isChild, 'type:', typeof isChild);
    const filtered = allMenuItems.filter(item => {
      // Check if isChild is truthy (true, 1, '1', 'true')
      if (isChild === true || isChild === 1 || isChild === '1' || isChild === 'true') {
        // Hide both redemptions and family relationships for children
        if (item.key === "redemptions" || item.key === "relationships") {
          console.log('🔍 Hiding menu item for child:', item.key);
          return false;
        }
      }
      return true;
    });
    console.log('🔍 Filtered menu items:', filtered.map(i => i.key).join(', '));
    return filtered;
  }, [isChild]);

  // Identity verification entry intentionally omitted from the menu; handled inside Withdrawals flow

  // Component to show when content creator access is disabled
  const ContentCreatorDisabledPage = () => (
    <Box 
      sx={{ 
        p: { xs: 3, sm: 5 }, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          opacity: 0.8,
        }}
      >
        <VideoLibraryIcon sx={{ fontSize: 40, color: 'white' }} />
      </Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
        Content Creator Access Disabled
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        Your content creator access is currently disabled by the administrator.
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: 'action.hover',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Please contact us at:
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            mt: 0.5,
          }}
        >
          admin@is-apps.com
        </Typography>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* Top AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", px: { xs: 1, sm: 3 }, py: { xs: 0.5, sm: 1 }, minHeight: { xs: '48px !important', sm: '64px' } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 2 }, flex: 1, minWidth: 0 }}>
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { xs: 'block', sm: 'none' },
                mr: { xs: 0.5, sm: 1 },
                padding: { xs: '6px', sm: '8px' },
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
            </IconButton>
            <Box sx={{ flex: { xs: 1, sm: 'none' }, minWidth: 0 }}>
              <DreamyAppsLogo size="medium" showText={true} variant="minimal" />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 0.25, sm: 1 }, flexShrink: 0 }}>
            {/* User Profile Section - Mobile: top, Desktop: right */}
            {userProfile && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 0.75, sm: 1.5 },
                px: { xs: 1, sm: 2 },
                py: { xs: 0.15, sm: 0.5 },
                borderRadius: { xs: 2, sm: 3 },
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Avatar 
                  sx={{ 
                    display: { xs: 'none', sm: 'flex' },
                    width: { xs: 28, sm: 36 }, 
                    height: { xs: 28, sm: 36 }, 
                    bgcolor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                  }}
                >
                  {userProfile.firstName?.[0]?.toUpperCase() || userProfile.username?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      lineHeight: 1.2, 
                      fontSize: { xs: '0.7rem', sm: '0.875rem' }, 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: { xs: 'clip', sm: 'ellipsis' }, 
                      maxWidth: { xs: '120px', sm: 'none' },
                      '&::after': { display: 'none' }
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      {(() => {
                        const fullName = userProfile.firstName && userProfile.lastName 
                          ? `${userProfile.firstName} ${userProfile.lastName}`
                          : userProfile.username;
                        return fullName.length > 15 ? fullName.substring(0, 15) : fullName;
                      })()}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {userProfile.firstName && userProfile.lastName 
                        ? `${userProfile.firstName} ${userProfile.lastName}`
                        : userProfile.username}
                    </Box>
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.6rem', sm: '0.7rem' }, display: { xs: 'none', sm: 'block' } }}>
                    {userProfile.email}
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Theme Toggle and Notification Icons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 }, alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
              <Divider orientation="vertical" flexItem sx={{ mx: { xs: 0, sm: 0.5 }, display: { xs: 'none', sm: 'flex' }, borderColor: 'rgba(255,255,255,0.2)' }} />
              {/* Theme Toggle Button */}
              <IconButton 
                onClick={onToggleDarkMode} 
                color="inherit" 
                sx={{ 
                  padding: { xs: '4px', sm: '8px' },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'scale(1.1) rotate(180deg)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? <Brightness7Icon sx={{ fontSize: { xs: '18px', sm: '24px' } }} /> : <Brightness4Icon sx={{ fontSize: { xs: '18px', sm: '24px' } }} />}
              </IconButton>
              {/* Notification Icon with Badge */}
              <IconButton 
                color="inherit" 
                onClick={() => showProfileIncompleteNotification && setNotificationDialogOpen(true)}
                sx={{ 
                  padding: { xs: '4px', sm: '8px' },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'scale(1.1)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <Badge 
                  badgeContent={showProfileIncompleteNotification ? 1 : 0} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.65rem',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                    }
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                </Badge>
              </IconButton>
            </Box>
            
            {isAdmin && (
              <Button 
                variant="outlined" 
                sx={{ 
                  ml: { xs: 0.5, sm: 1 },
                  mr: { xs: 0.5, sm: 1 },
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(255,255,255,0.2)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
                onClick={() => navigate("/admin/dashboard")}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Admin&nbsp;</Box>
                <Box component="span">Dashboard</Box>
              </Button>
            )}
            
            {isDirector && (
              <Button 
                variant="outlined" 
                sx={{ 
                  ml: { xs: 0.5, sm: 1 },
                  mr: { xs: 0.5, sm: 1 },
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  padding: { xs: '4px 8px', sm: '6px 16px' },
                  minWidth: { xs: 'auto', sm: '64px' },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.15)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(255,255,255,0.2)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
                onClick={() => navigate("/director/dashboard")}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Directors&nbsp;</Box>
                <Box component="span">Dashboard</Box>
              </Button>
            )}
            {/* Logout Button - Full on Desktop, Icon only on Mobile */}
            <Button 
              variant="contained" 
              sx={{ 
                display: { xs: 'none', md: 'flex' }, // Full button on desktop
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255,255,255,0.2)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
              onClick={onLogout}
              startIcon={<LogoutIcon />}
            >
              Logout
            </Button>
            
            {/* Logout Icon Button - Hidden on mobile */}
            <IconButton
              color="inherit"
              onClick={onLogout}
              sx={{ 
                display: 'none', // Hidden on all devices
                ml: 0.5,
                padding: { xs: '6px', sm: '8px' },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
              title="Logout"
            >
              <LogoutIcon sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer (Temporary) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: { xs: mobileDrawerWidth, sm: drawerWidth },
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 1.5, sm: 3 }, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', opacity: 0.9 }}>
          <Chip 
            label="User Dashboard" 
            size="small" 
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              fontWeight: 600,
              mb: { xs: 1, sm: 2 },
              width: '100%',
              height: { xs: '28px', sm: '32px' },
              fontSize: { xs: '0.7rem', sm: '0.8rem' }
            }} 
          />
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: 'text.primary', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Navigation Menu
          </Typography>
        </Box>
        <List sx={{ px: { xs: 0.5, sm: 2 }, py: { xs: 2, sm: 3 } }}>
          {menuItems.map((item, index) => {
            // Profile completion check removed - users can navigate freely
            // Only redemption page (WithdrawHub) enforces profile completion
            const isDisabled = false;
            
            return (
            <ListItemButton
              key={item.key}
                component={NavLink}
                to={`/dashboard/${item.key}`}
                onClick={handleMobileNavClick}
              sx={{
                borderRadius: 3,
                mb: 1.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.12)",
                    transform: 'translateX(8px) scale(1.02)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                },
                "&.active": {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: "white",
                  fontWeight: "bold",
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  transform: 'translateX(4px)',
                  "& .MuiListItemIcon-root": {
                    color: "white",
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
            );
          })}

          {/* Content Creator Menu Items - Shown to all users */}
          <Divider sx={{ my: { xs: 1, sm: 1.5 }, borderColor: 'divider' }} />
          
          <ListItemButton
            key="channels"
            component={NavLink}
            to="/dashboard/channels"
            onClick={(e) => { handleContentCreatorClick(e); if (isContentCreator) handleMobileNavClick(); }}
            sx={{
              borderRadius: 2,
              mb: { xs: 0.75, sm: 1 },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              transition: 'all 0.2s ease-in-out',
              opacity: isContentCreator ? 1 : 0.7,
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                transform: 'translateX(4px)',
              },
              "&.active": {
                background: isContentCreator ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: isContentCreator ? "white" : "inherit",
                fontWeight: isContentCreator ? "bold" : "normal",
                boxShadow: isContentCreator ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                "& .MuiListItemIcon-root": {
                  color: isContentCreator ? "white" : "inherit",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 }, mr: { xs: 0.5, sm: 1 } }}>
              <VideoCallIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Channels" 
              primaryTypographyProps={{
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                fontWeight: 500,
              }}
            />
          </ListItemButton>

          {/* Video Upload menu item */}
          <ListItemButton
            key="video-upload"
            component={NavLink}
            to="/dashboard/video-upload"
            onClick={(e) => { handleContentCreatorClick(e); if (isContentCreator) handleMobileNavClick(); }}
            sx={{
              borderRadius: 2,
              mb: { xs: 0.75, sm: 1 },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              transition: 'all 0.2s ease-in-out',
              opacity: isContentCreator ? 1 : 0.7,
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                transform: 'translateX(4px)',
              },
              "&.active": {
                background: isContentCreator ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: isContentCreator ? "white" : "inherit",
                fontWeight: isContentCreator ? "bold" : "normal",
                boxShadow: isContentCreator ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                "& .MuiListItemIcon-root": {
                  color: isContentCreator ? "white" : "inherit",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 }, mr: { xs: 0.5, sm: 1 } }}>
              <VideoLibraryIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Upload Video" 
              primaryTypographyProps={{
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                fontWeight: 500,
              }}
            />
          </ListItemButton>

          <ListItemButton
            key="my-videos"
            component={NavLink}
            to="/dashboard/my-videos"
            onClick={(e) => { handleContentCreatorClick(e); if (isContentCreator) handleMobileNavClick(); }}
            sx={{
              borderRadius: 2,
              mb: { xs: 0.75, sm: 1 },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              transition: 'all 0.2s ease-in-out',
              opacity: isContentCreator ? 1 : 0.7,
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                transform: 'translateX(4px)',
              },
              "&.active": {
                background: isContentCreator ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: isContentCreator ? "white" : "inherit",
                fontWeight: isContentCreator ? "bold" : "normal",
                boxShadow: isContentCreator ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                "& .MuiListItemIcon-root": {
                  color: isContentCreator ? "white" : "inherit",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 }, mr: { xs: 0.5, sm: 1 } }}>
              <VideoLibraryIcon />
            </ListItemIcon>
            <ListItemText 
              primary="My Videos" 
              primaryTypographyProps={{
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                fontWeight: 500,
              }}
            />
          </ListItemButton>
          
          {/* Logout Button in Mobile Drawer */}
          <Divider sx={{ my: { xs: 1, sm: 1.5 }, borderColor: 'divider' }} />
          <ListItemButton
            onClick={() => {
              handleMobileNavClick();
              onLogout();
            }}
            sx={{
              borderRadius: 3,
              mb: { xs: 1, sm: 1.5 },
              px: { xs: 1, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)',
              "&:hover": {
                background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                transform: 'translateX(8px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(245, 87, 108, 0.4)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 32, sm: 40 }, mr: { xs: 0.5, sm: 1 }, color: 'white' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Sidebar Drawer (Desktop - Permanent) */}
        <Drawer
          variant="permanent"
          sx={{
            width: { xs: 0, sm: drawerWidth },
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              position: "relative",
              // Theme-aware background
            },
          }}
        >
        <Toolbar />
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', opacity: 0.9 }}>
          <Chip 
            label="User Dashboard" 
            size="small" 
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              fontWeight: 600,
              mb: 2,
              width: '100%',
              height: '32px',
              fontSize: { xs: '0.7rem', sm: '0.8rem' }
            }} 
          />
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: 'text.primary', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Navigation Menu
          </Typography>
        </Box>
          <List sx={{ px: 2, py: 3 }}>
            {menuItems.map((item, index) => {
              // Profile completion check removed - users can navigate freely
              // Only redemption page (WithdrawHub) enforces profile completion
              const isDisabled = false;
              
              return (
              <ListItemButton
                key={item.key}
                  component={NavLink}
                  to={`/dashboard/${item.key}`}
                sx={{
                  borderRadius: 3,
                  mb: 1.5,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `slideInLeft 0.5s ease-out ${index * 0.1}s both`,
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                  "&:hover": {
                      backgroundColor: isDisabled ? "transparent" : "rgba(102, 126, 234, 0.12)",
                      transform: isDisabled ? 'none' : 'translateX(8px) scale(1.02)',
                      boxShadow: isDisabled ? 'none' : '0 8px 25px rgba(102, 126, 234, 0.15)',
                    "&::before": {
                        opacity: isDisabled ? 0 : 1,
                        transform: isDisabled ? 'scaleX(0)' : 'scaleX(1)',
                    },
                  },
                  "&.active": {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: "white",
                    fontWeight: "bold",
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                    transform: 'translateX(4px)',
                    "& .MuiListItemIcon-root": {
                      color: "white",
                      transform: 'scale(1.1)',
                    },
                    "&::after": {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 4,
                      height: '60%',
                      background: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '2px 0 0 2px',
                    },
                  },
                  "&::before": {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'all 0.3s ease',
                    opacity: 0,
                  },
                }}
              >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
              );
            })}

          {/* Content Creator Menu Items - Shown to all users */}
          <Divider sx={{ my: 1.5, borderColor: 'divider' }} />
          
          <ListItemButton
            key="channels"
            component={NavLink}
            to="/dashboard/channels"
            onClick={handleContentCreatorClick}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: 'all 0.2s ease-in-out',
              opacity: isContentCreator ? 1 : 0.7,
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                transform: 'translateX(4px)',
              },
              "&.active": {
                background: isContentCreator ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: isContentCreator ? "white" : "inherit",
                fontWeight: isContentCreator ? "bold" : "normal",
                boxShadow: isContentCreator ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                "& .MuiListItemIcon-root": {
                  color: isContentCreator ? "white" : "inherit",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <VideoCallIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Channels" 
              primaryTypographyProps={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 500,
              }}
            />
          </ListItemButton>

          {/* Video Upload menu item */}
          <ListItemButton
            key="video-upload"
            component={NavLink}
            to="/dashboard/video-upload"
            onClick={handleContentCreatorClick}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: 'all 0.2s ease-in-out',
              opacity: isContentCreator ? 1 : 0.7,
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                transform: 'translateX(4px)',
              },
              "&.active": {
                background: isContentCreator ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: isContentCreator ? "white" : "inherit",
                fontWeight: isContentCreator ? "bold" : "normal",
                boxShadow: isContentCreator ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                "& .MuiListItemIcon-root": {
                  color: isContentCreator ? "white" : "inherit",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <VideoLibraryIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Upload Video" 
              primaryTypographyProps={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 500,
              }}
            />
          </ListItemButton>

          <ListItemButton
            key="my-videos"
            component={NavLink}
            to="/dashboard/my-videos"
            onClick={handleContentCreatorClick}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: 'all 0.2s ease-in-out',
              opacity: isContentCreator ? 1 : 0.7,
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                transform: 'translateX(4px)',
              },
              "&.active": {
                background: isContentCreator ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                color: isContentCreator ? "white" : "inherit",
                fontWeight: isContentCreator ? "bold" : "normal",
                boxShadow: isContentCreator ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                "& .MuiListItemIcon-root": {
                  color: isContentCreator ? "white" : "inherit",
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <VideoLibraryIcon />
            </ListItemIcon>
            <ListItemText 
              primary="My Videos" 
              primaryTypographyProps={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh',
          position: 'relative',
          overflowX: 'hidden', // Prevent horizontal scroll on mobile
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ p: { xs: 2, sm: 4 }, position: 'relative', zIndex: 1 }}>
          <Paper 
            elevation={0}
            sx={{
              borderRadius: 3,
              bgcolor: 'background.paper',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
            }}
          >
            <Routes>
              <Route path="engagement" element={<EarningsSummary />} />
              
              {/* Unified Withdrawals hub */}
              <Route path="redemptions" element={<WithdrawHub />} />
              <Route path="profile" element={<UpdateProfile />} />
              <Route path="kyc-verification" element={<StripeKYCVerification />} />
              <Route path="kyc-test" element={<KYCTestComponent />} />
              <Route path="invite" element={<InviteFriends />} />
              <Route path="community" element={<ReferralTree />} />
              <Route path="relationships" element={<ParentChildRelationships />} />
              <Route path="activity" element={<TokenLedger />} />
              <Route path="apps" element={<Apps />} />
              {/* Content Creator Routes - Show message for non-content creators */}
              <Route path="video-upload" element={
                isContentCreator ? <VideoUpload /> : <ContentCreatorDisabledPage />
              } />
              <Route path="my-videos" element={
                isContentCreator ? <UserVideoList /> : <ContentCreatorDisabledPage />
              } />
              
              {/* Channel routes */}
              <Route path="channels" element={
                isContentCreator ? <ChannelsList /> : <ContentCreatorDisabledPage />
              } />
              <Route path="channels/:channelId" element={<ChannelDetail />} />
              <Route path="channels/:channelId/manage" element={<ChannelManage />} />
              <Route path="channels/:channelId/edit" element={<ChannelEdit />} />
              <Route path="videos/:videoId/edit" element={<VideoEdit />} />
              
              <Route path="*" element={<Navigate to="engagement" replace />} />
            </Routes>
          </Paper>
        </Container>
      </Box>
      
      {/* Profile Incomplete Notification Dialog */}
      <Dialog 
        open={notificationDialogOpen} 
        onClose={() => setNotificationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="error" />
            <span style={{ color: '#dc2626' }}>Important Notice</span>
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                width: '100%',
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {profileIncomplete && redemptionIncomplete 
                ? <>Please update your <span style={{ color: '#eab308', fontWeight: 600 }}>Profile</span> and <span style={{ color: '#eab308', fontWeight: 600 }}>Redemptions</span> as soon as possible for payments.</>
                : profileIncomplete 
                  ? <>Please update your <span style={{ color: '#eab308', fontWeight: 600 }}>Profile</span> as soon as possible.</>
                  : <>Please update your <span style={{ color: '#eab308', fontWeight: 600 }}>Redemptions</span> as soon as possible</>}
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {profileIncomplete && redemptionIncomplete 
              ? <>To complete your setup, please go to the <span style={{ color: '#eab308', fontWeight: 600 }}>Profile</span> page to update your information and the <span style={{ color: '#eab308', fontWeight: 600 }}>Redemptions</span> page to add your payment details including your CNIC number.</>
              : profileIncomplete 
                ? <>To complete your profile, please go to the <span style={{ color: '#eab308', fontWeight: 600 }}>Profile</span> page and update your information.</>
                : <>To receive payments, please go to the <span style={{ color: '#eab308', fontWeight: 600 }}>Redemptions</span> page and add your payment information including your CNIC number.</>}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setNotificationDialogOpen(false)} color="inherit">
            Later
          </Button>
          {profileIncomplete && (
            <Button 
              variant="contained" 
              onClick={() => {
                setNotificationDialogOpen(false);
                navigate('/dashboard/profile');
              }}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              Go to Profile
            </Button>
          )}
          {redemptionIncomplete && (
            <Button 
              variant="contained" 
              onClick={() => {
                setNotificationDialogOpen(false);
                navigate('/dashboard/redemptions');
              }}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              Go to Redemptions
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserDashboard;
